// Copyright 2022, Imprivata, Inc.  All rights reserved.

/**
 * Returns true for phones & tablets; false for laptops/desktops/TVs/consoles.
 * - SSR-safe (no window/navigator assumptions).
 * - Modern iPad support (iPadOS 13+ identifies as "Macintosh" + touch).
 * - Avoids giant legacy regexes; uses a few targeted UA checks + capability fallbacks.
 */
export function isMobileDeviceDetected(): boolean {
  // SSR / non-browser guard
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return false;
  }

  const ua = navigator.userAgent || "";
  // biome-ignore lint/suspicious/noExplicitAny: userAgentData is experimental API not in all type definitions
  const brandsMobile = (navigator as any).userAgentData?.mobile === true;

  // Helper to safely query media features
  const mq = (q: string) =>
    typeof window.matchMedia === "function"
      ? window.matchMedia(q).matches
      : false;

  // 1) Quick exclusions (TVs / consoles)
  const isTV =
    /\b(?:SmartTV|HbbTV|Tizen|BRAVIA|AFT|Android TV|GoogleTV|TV)\b/i.test(ua);
  const isConsole = /\b(?:Xbox|PlayStation|Nintendo)\b/i.test(ua);
  if (isTV || isConsole) {
    return false;
  }

  // 2) Strong positive signals (phones/tablets)
  // iOS/iPadOS
  const isIPad =
    /\biPad\b/i.test(ua) ||
    (/\bMacintosh\b/i.test(ua) && (navigator.maxTouchPoints || 0) > 1); // iPadOS 13+

  const isIPhoneOrIPod = /\b(iPhone|iPod)\b/i.test(ua);

  // Android (exclude non-handheld variants)
  const isAndroid =
    /\bAndroid\b/i.test(ua) &&
    !/\b(?:Android TV|GoogleTV|BRAVIA|AFT|Auto|Automotive)\b/i.test(ua);

  // Kindle / Amazon Fire tablets
  const isKindleOrSilk = /\b(?:Kindle|Silk)\b/i.test(ua);

  // Legacy Windows Phone
  const isWindowsPhone = /\bWindows Phone\b/i.test(ua);

  // Windows tablets (heuristic): Windows + Touch token (avoid Xbox)
  const isWindowsTablet =
    /\bWindows NT\b/i.test(ua) &&
    /\bTouch\b/i.test(ua) &&
    !/\bXbox\b/i.test(ua);

  // ChromeOS tablet heuristic: CrOS + coarse pointer and no fine pointer
  const isChromeOSTablet =
    /\bCrOS\b/i.test(ua) &&
    mq("(any-pointer: coarse)") &&
    !mq("(any-pointer: fine)");

  // 3) Capability fallback (for ambiguous UAs)
  // Phones/tablets typically have:
  //  - primary coarse pointer
  //  - no hover anywhere
  //  - multi-touch
  const hasCoarsePrimary = mq("(pointer: coarse)");
  const noHoverAnywhere = mq("(any-hover: none)");
  const hasMultiTouch = (navigator.maxTouchPoints || 0) >= 2;
  const capabilityMobile = hasCoarsePrimary && noHoverAnywhere && hasMultiTouch;

  // 4) Final decision
  return (
    isIPad ||
    isIPhoneOrIPod ||
    isAndroid ||
    isKindleOrSilk ||
    isWindowsPhone ||
    isWindowsTablet ||
    isChromeOSTablet ||
    brandsMobile || // UA-CH: true for smartphones in Chromium
    capabilityMobile // last-resort capability heuristic
  );
}
